/**
 * CreateTestSeriesPage — build or edit a Test Series: meta, progression mode,
 * assignment, and the ordered list of member tests (existing Assessments).
 * Design-system primitives + theme tokens only.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Layers, Save, Send, Plus, X, ArrowUp, ArrowDown, Unlock, Calendar, Lock,
  BookOpen, Globe2, Trophy, Search, ListChecks,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { toDateTimeLocal, fromDateTimeLocal } from "../../utils/datetime";
import {
  PageHeader, AppButton, AppCard, AppCardContent, AppModal, EmptyState, SearchInput,
  FormActionBar, ResetButton, Switch,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import { parentOf, seriesFormTrail } from "../QuizBank/quizCenterTrail";
import "./TestSeries.scss";

const UNLOCK_MODES = [
  { key: "open", label: "Open", icon: Unlock, sub: "All tests available at once, any order." },
  { key: "scheduled", label: "Scheduled", icon: Calendar, sub: "Each test unlocks on its own date." },
  { key: "sequential", label: "Sequential", icon: Lock, sub: "Next unlocks after finishing the previous." },
];

const emptyForm = {
  title: "", description: "", instructions: "", category: "",
  unlockMode: "open", sequentialRequiresPass: false,
  assignmentType: "batch", assignedBatches: [], assignedCourses: [],
  startDate: "", endDate: "", leaderboardEnabled: true,
  tests: [],
};

const CreateTestSeriesPage = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  // Pristine snapshot used by the Reset action (empty form, or the loaded
  // series when editing).
  const pristineRef = useRef(emptyForm);

  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);

  // picker modal
  const [pickerOpen, setPickerOpen] = useState(false);
  const [available, setAvailable] = useState([]);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerLoading, setPickerLoading] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Reference data (cross-branch)
  useEffect(() => {
    const allBranches = { headers: { "X-Branch-Id": "all" } };
    Promise.all([
      apiClient.get("/batches/list?pageSize=500", allBranches).catch(() => ({ data: { batches: [] } })),
      apiClient.get("/courses/list?pageSize=500", allBranches).catch(() => ({ data: { courses: [] } })),
    ]).then(([bat, crs]) => {
      setBatches(bat.data.batches || []);
      setCourses(crs.data.courses || []);
    });
  }, []);

  // Edit-mode load
  useEffect(() => {
    if (!isEdit) return;
    setLoadingData(true);
    apiClient.get(`/test-series/${id}`, { headers: { "X-Branch-Id": "all" } })
      .then(({ data }) => {
        const s = data.series;
        const loaded = {
          title: s.title || "",
          description: s.description || "",
          instructions: s.instructions || "",
          category: s.category || "",
          unlockMode: s.unlockMode || "open",
          sequentialRequiresPass: s.sequentialRequiresPass || false,
          assignmentType: s.assignmentType || "batch",
          assignedBatches: (s.assignedBatches || []).map((b) => b._id || b),
          assignedCourses: (s.assignedCourses || []).map((c) => c._id || c),
          startDate: toDateTimeLocal(s.startDate),
          endDate: toDateTimeLocal(s.endDate),
          leaderboardEnabled: s.leaderboardEnabled !== false,
          tests: (s.tests || []).map((t) => ({
            assessment: t.assessment?._id || t.assessment,
            title: t.assessment?.title || t.label || "Test",
            mode: t.assessment?.mode || "",
            questionCount: t.assessment?.questionCount || 0,
            timeLimit: t.assessment?.timeLimit || 0,
            status: t.assessment?.status || "",
            isOptional: !!t.isOptional,
            unlockDate: toDateTimeLocal(t.unlockDate),
            label: t.label || "",
          })),
        };
        pristineRef.current = loaded;
        setForm(loaded);
      })
      .catch(() => setToast({ show: true, message: "Failed to load series", type: "error" }))
      .finally(() => setLoadingData(false));
  }, [id, isEdit]);

  // Picker search (published assessments not yet added)
  const searchAssessments = useCallback(async () => {
    setPickerLoading(true);
    try {
      const params = { status: "published", limit: 100, sort: "newest" };
      if (pickerSearch) params.search = pickerSearch;
      const { data } = await apiClient.get("/assessments", { params, headers: { "X-Branch-Id": "all" } });
      setAvailable(data.assessments || []);
    } catch { /* silent */ }
    setPickerLoading(false);
  }, [pickerSearch]);

  useEffect(() => { if (pickerOpen) searchAssessments(); }, [pickerOpen, searchAssessments]);

  const addedIds = new Set(form.tests.map((t) => t.assessment));

  const addTest = (a) => {
    if (addedIds.has(a._id)) return;
    setForm((p) => ({
      ...p,
      tests: [...p.tests, {
        assessment: a._id, title: a.title, mode: a.mode,
        questionCount: a.questionCount || 0, timeLimit: a.timeLimit || 0,
        status: a.status, isOptional: false, unlockDate: "", label: "",
      }],
    }));
  };

  const removeTest = (idx) => setForm((p) => ({ ...p, tests: p.tests.filter((_, i) => i !== idx) }));

  const moveTest = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= form.tests.length) return;
    setForm((p) => {
      const next = [...p.tests];
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...p, tests: next };
    });
  };

  const patchTest = (idx, patch) => setForm((p) => {
    const next = [...p.tests];
    next[idx] = { ...next[idx], ...patch };
    return { ...p, tests: next };
  });

  const handleSave = async (publish = false) => {
    if (!form.title.trim()) {
      setToast({ show: true, message: "Title is required", type: "error" });
      return;
    }
    if (form.assignmentType === "batch" && form.assignedBatches.length === 0) {
      setToast({ show: true, message: "Select at least one batch", type: "error" });
      return;
    }
    if (form.assignmentType === "course" && form.assignedCourses.length === 0) {
      setToast({ show: true, message: "Select at least one course", type: "error" });
      return;
    }
    if (publish && form.tests.length === 0) {
      setToast({ show: true, message: "Add at least one test before publishing", type: "error" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        startDate: fromDateTimeLocal(form.startDate),
        endDate: fromDateTimeLocal(form.endDate),
        tests: form.tests.map((t, i) => ({
          assessment: t.assessment,
          order: i,
          isOptional: t.isOptional,
          unlockDate: form.unlockMode === "scheduled" ? fromDateTimeLocal(t.unlockDate) : null,
          label: t.label || "",
        })),
      };

      let sid;
      if (isEdit) {
        await apiClient.put(`/test-series/${id}`, payload);
        sid = id;
        setToast({ show: true, message: "Series updated", type: "success" });
      } else {
        const { data } = await apiClient.post("/test-series", payload);
        sid = data.series._id;
        setToast({ show: true, message: "Series created", type: "success" });
      }
      if (publish && sid) {
        await apiClient.put(`/test-series/${sid}/publish`);
        setToast({ show: true, message: "Series published!", type: "success" });
      }
      setTimeout(() => navigate("/test-series"), 700);
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to save", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setForm(pristineRef.current);

  // Named as saved, not as it is being typed. Cancel goes up it, like the arrow.
  const trail = seriesFormTrail({ id, title: pristineRef.current.title });

  if (loadingData) {
    return (
      <div className="ts-create">
        <PageHeader icon={Layers} title="Edit test series" breadcrumbs={trail} />
        <div className="text-center py-5">Loading…</div>
      </div>
    );
  }

  return (
    <div className="ts-create has-form-actionbar">
      <PageHeader
        icon={Layers}
        title={isEdit ? "Edit test series" : "Create test series"}
        description="Bundle multiple tests into one series · saved as draft until you publish"
        breadcrumbs={trail}
      />

      {/* Basics */}
      <AppCard>
        <AppCardContent>
          <div className="ts-field">
            <span className="ts-label">Series title *</span>
            <input className="ts-input" value={form.title} autoFocus
              onChange={(e) => update("title", e.target.value)}
              placeholder="e.g. NEET 2026 Grand Test Series" />
          </div>
          <div className="ts-grid-2">
            <div className="ts-field">
              <span className="ts-label">Category</span>
              <input className="ts-input" value={form.category}
                onChange={(e) => update("category", e.target.value)}
                placeholder="e.g. NEET, EMCET, Aptitude, Reasoning, Coding" />
            </div>
            <div className="ts-field">
              <span className="ts-label">Description</span>
              <input className="ts-input" value={form.description}
                onChange={(e) => update("description", e.target.value)}
                placeholder="Short summary shown to students" />
            </div>
          </div>
          <div className="ts-field">
            <span className="ts-label">Instructions</span>
            <textarea className="ts-textarea" rows={2} value={form.instructions}
              onChange={(e) => update("instructions", e.target.value)}
              placeholder="General instructions for the whole series" />
          </div>
        </AppCardContent>
      </AppCard>

      {/* Progression */}
      <AppCard>
        <AppCardContent>
          <div className="ts-section-title"><Lock size={15} /> Progression</div>
          <div className="ts-modes">
            {UNLOCK_MODES.map((m) => {
              const Icon = m.icon;
              const active = form.unlockMode === m.key;
              return (
                <button key={m.key} type="button"
                  className={`ts-mode${active ? " ts-mode--active" : ""}`}
                  onClick={() => update("unlockMode", m.key)}>
                  <span className="ts-mode__label"><Icon size={15} /> {m.label}</span>
                  <span className="ts-mode__sub">{m.sub}</span>
                </button>
              );
            })}
          </div>
          {form.unlockMode === "sequential" && (
            <div className="d-flex align-items-center justify-content-between mt-3"
              style={{ maxWidth: 420 }}>
              <span className="ts-label">Require <strong>passing</strong> the previous test (not just completing)</span>
              <Switch checked={form.sequentialRequiresPass} onChange={(v) => update("sequentialRequiresPass", v)} aria-label="Require passing the previous test" />
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* Member tests */}
      <AppCard>
        <AppCardContent>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="ts-section-title mb-0"><ListChecks size={15} /> Tests in this series ({form.tests.length})</div>
            <AppButton variant="default" size="sm" onClick={() => setPickerOpen(true)}>
              <Plus size={14} /> Add test
            </AppButton>
          </div>
          <p className="ts-mode__sub mb-3">
            Add existing published assessments. Build new ones (directly or via Excel import) from the{" "}
            <button type="button" className="ts-icon-btn" style={{ textDecoration: "underline", padding: 0 }}
              onClick={() => navigate("/assessments/create")}>Assessments</button>{" "}
            screen, then add them here — or import a whole series at once via <strong>Import Excel</strong>.
          </p>

          {form.tests.length === 0 ? (
            <EmptyState icon={<ListChecks size={32} />} title="No tests yet"
              description="Click “Add test” to bundle assessments into this series." />
          ) : (
            <div className="ts-tests">
              {form.tests.map((t, idx) => (
                <div className="ts-test-row" key={t.assessment}>
                  <span className="ts-test-num">{idx + 1}</span>
                  <div className="ts-test-body">
                    <div className="ts-test-title">{t.title}</div>
                    <div className="ts-test-meta">
                      {t.mode && <span>{t.mode}</span>}
                      <span>{t.questionCount} Q</span>
                      {t.timeLimit > 0 && <span>{t.timeLimit} min</span>}
                      <label className="d-inline-flex align-items-center gap-1" style={{ cursor: "pointer" }}>
                        <input type="checkbox" checked={t.isOptional}
                          onChange={(e) => patchTest(idx, { isOptional: e.target.checked })} />
                        Optional
                      </label>
                      {form.unlockMode === "scheduled" && (
                        <label className="d-inline-flex align-items-center gap-1">
                          Unlocks:
                          <input type="datetime-local" className="ts-input" style={{ width: 200, padding: "4px 8px" }}
                            value={t.unlockDate}
                            onChange={(e) => patchTest(idx, { unlockDate: e.target.value })} />
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="ts-test-controls">
                    <button type="button" className="ts-icon-btn" disabled={idx === 0} onClick={() => moveTest(idx, -1)} aria-label="Move up"><ArrowUp size={15} /></button>
                    <button type="button" className="ts-icon-btn" disabled={idx === form.tests.length - 1} onClick={() => moveTest(idx, 1)} aria-label="Move down"><ArrowDown size={15} /></button>
                    <button type="button" className="ts-icon-btn" onClick={() => removeTest(idx)} aria-label="Remove"><X size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* Assignment */}
      <AppCard>
        <AppCardContent>
          <div className="ts-section-title"><BookOpen size={15} /> Assign to students</div>
          <div className="ts-assign-types">
            {[
              { k: "batch", l: "Batches", i: Layers },
              { k: "course", l: "Courses", i: BookOpen },
              { k: "open", l: "Open to all", i: Globe2 },
            ].map((opt) => {
              const Icon = opt.i;
              const active = form.assignmentType === opt.k;
              return (
                <button key={opt.k} type="button"
                  className={`ts-assign-type${active ? " ts-assign-type--active" : ""}`}
                  onClick={() => { update("assignmentType", opt.k); update("assignedBatches", []); update("assignedCourses", []); }}>
                  <Icon size={14} /> {opt.l}
                </button>
              );
            })}
          </div>

          {form.assignmentType === "batch" && (
            <div className="ts-checklist">
              {batches.length === 0 ? <div className="ts-check-row">No batches found.</div> : batches.map((b) => {
                const checked = form.assignedBatches.includes(b._id);
                return (
                  <label key={b._id} className={`ts-check-row${checked ? " ts-check-row--on" : ""}`}>
                    <input type="checkbox" checked={checked}
                      onChange={(e) => update("assignedBatches", e.target.checked
                        ? [...form.assignedBatches, b._id]
                        : form.assignedBatches.filter((x) => x !== b._id))} />
                    {b.name}
                  </label>
                );
              })}
            </div>
          )}
          {form.assignmentType === "course" && (
            <div className="ts-checklist">
              {courses.length === 0 ? <div className="ts-check-row">No courses found.</div> : courses.map((c) => {
                const checked = form.assignedCourses.includes(c._id);
                return (
                  <label key={c._id} className={`ts-check-row${checked ? " ts-check-row--on" : ""}`}>
                    <input type="checkbox" checked={checked}
                      onChange={(e) => update("assignedCourses", e.target.checked
                        ? [...form.assignedCourses, c._id]
                        : form.assignedCourses.filter((x) => x !== c._id))} />
                    {c.title || c.name}
                  </label>
                );
              })}
            </div>
          )}
          {form.assignmentType === "open" && (
            <p className="ts-mode__sub">Available to every student in your institute (across all branches).</p>
          )}

          <div className="ts-grid-2" style={{ marginTop: 14 }}>
            <div className="ts-field">
              <span className="ts-label">Available from</span>
              <input type="datetime-local" className="ts-input" value={form.startDate}
                onChange={(e) => update("startDate", e.target.value)} />
            </div>
            <div className="ts-field">
              <span className="ts-label">Available until</span>
              <input type="datetime-local" className="ts-input" value={form.endDate}
                onChange={(e) => update("endDate", e.target.value)} />
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between" style={{ maxWidth: 420 }}>
            <span className="ts-label d-inline-flex align-items-center gap-1"><Trophy size={14} /> Show series leaderboard</span>
            <Switch checked={form.leaderboardEnabled} onChange={(v) => update("leaderboardEnabled", v)} aria-label="Show series leaderboard" />
          </div>
        </AppCardContent>
      </AppCard>

      {/* Actions */}
      <FormActionBar>
        <AppButton variant="outline" type="button" onClick={() => navigate(parentOf(trail))}>Cancel</AppButton>
        <ResetButton onReset={handleReset} disabled={saving} />
        <AppButton variant="outline" onClick={() => handleSave(false)} disabled={saving || !form.title}>
          <Save size={14} /> {saving ? "Saving…" : "Save draft"}
        </AppButton>
        <AppButton variant="default" onClick={() => handleSave(true)} disabled={saving || !form.title || form.tests.length === 0}>
          <Send size={14} /> {saving ? "Saving…" : "Save & publish"}
        </AppButton>
      </FormActionBar>

      {/* Test picker modal */}
      <AppModal show={pickerOpen} onHide={() => setPickerOpen(false)} title="Add a test" size="lg" icon={<Search size={18} />}>
        <div className="mb-3">
          <SearchInput value={pickerSearch} onChange={setPickerSearch} placeholder="Search published assessments…" />
        </div>
        {pickerLoading ? (
          <div className="text-center py-4">Loading…</div>
        ) : available.length === 0 ? (
          <EmptyState icon={<Search size={32} />} title="No published assessments"
            description="Create and publish assessments first — they'll appear here to add." />
        ) : (
          <div className="ts-tests">
            {available.map((a) => {
              const already = addedIds.has(a._id);
              return (
                <div className="ts-test-row" key={a._id}>
                  <div className="ts-test-body">
                    <div className="ts-test-title">{a.title}</div>
                    <div className="ts-test-meta">
                      {a.mode && <span>{a.mode}</span>}
                      <span>{a.questionCount || 0} Q</span>
                      {a.timeLimit > 0 && <span>{a.timeLimit} min</span>}
                    </div>
                  </div>
                  <AppButton variant={already ? "outline" : "default"} size="sm"
                    disabled={already} onClick={() => addTest(a)}>
                    {already ? "Added" : <><Plus size={14} /> Add</>}
                  </AppButton>
                </div>
              );
            })}
          </div>
        )}
      </AppModal>

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default CreateTestSeriesPage;
